import React from "react";
import { Alert, Button } from "antd";

// Key used in sessionStorage to track whether we already auto-reloaded
// for a specific chunk error. Prevents infinite reload loops.
const CHUNK_RELOAD_KEY = "app_chunk_reload_attempted";

// Force a hard reload by appending a timestamp query param.
// This bypasses iOS Safari's aggressive memory/disk cache which may
// serve a stale index.html even after a normal location.reload().
const hardReload = () => {
  const url = new URL(window.location.href);
  url.searchParams.set("_cb", Date.now());
  window.location.replace(url.toString());
};

const isChunkError = (error) => {
  if (!error) return false;
  const msg = error.message || "";
  return (
    msg.includes("Loading chunk") ||
    msg.includes("Failed to fetch dynamically imported module") ||
    msg.includes("Importing a module script failed") ||
    msg.includes("ChunkLoadError")
  );
};

class AppErrorBoundary extends React.Component {
  state = {
    error: null,
  };

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.resetKey !== this.props.resetKey &&
      this.state.error !== null
    ) {
      this.setState({ error: null });
    }
  }

  componentDidCatch(error, info) {
    console.error("App route render failed:", error, info);

    // Auto hard-reload once when a chunk fails to load.
    // Uses a timestamp URL param so iOS Safari is forced to bypass its cache
    // and fetch a fresh index.html with the correct chunk hashes.
    if (isChunkError(error)) {
      const alreadyReloaded = sessionStorage.getItem(CHUNK_RELOAD_KEY);
      if (!alreadyReloaded) {
        sessionStorage.setItem(CHUNK_RELOAD_KEY, "1");
        hardReload();
      }
    }
  }

  handleManualReload = () => {
    // Clear the flag so a manual reload can also trigger the auto-retry
    sessionStorage.removeItem(CHUNK_RELOAD_KEY);
    hardReload();
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const chunkError = isChunkError(error);

    return (
      <div style={{ padding: 16 }}>
        <Alert
          type="error"
          showIcon
          message="Không thể tải trang"
          description={
            chunkError
              ? "Ứng dụng vừa được cập nhật. Vui lòng nhấn 'Tải lại' để tiếp tục."
              : error?.message ||
                "Trình duyệt không tương thích hoặc gói giao diện tải chưa đầy đủ. Vui lòng tải lại trang."
          }
          action={
            <Button size="small" danger onClick={this.handleManualReload}>
              Tải lại
            </Button>
          }
        />
      </div>
    );
  }
}

export default AppErrorBoundary;
